import { useEffect, useRef } from "react";

import { DeferredCommitDateInput } from "@/components/mrp/DeferredCommitDateInput";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { isMrpTransferRequestEditable } from "@/lib/mrp";
import { cn } from "@/lib/utils";

import type { TransferBoardRequest } from "./transfersWorkbenchTypes";

const TARGET_DATE_LOCKED_HOVER =
  "Only open transfer requests can have their target date changed.";

export function TransferRequestTargetDateCell({
  row,
  hubWritesAllowed,
  hubReadOnlyHoverHint,
  editing,
  onStartEdit,
  onDismissEdit,
  onCommit,
  pending,
  commitError,
}: {
  row: TransferBoardRequest;
  hubWritesAllowed: boolean;
  hubReadOnlyHoverHint: string;
  editing: boolean;
  onStartEdit: () => void;
  onDismissEdit: () => void;
  onCommit: (value: string | null) => void;
  pending: boolean;
  commitError: string | null;
}): JSX.Element {
  const editable = hubWritesAllowed && isMrpTransferRequestEditable(row.status);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;
    const input = editorRef.current?.querySelector('input[type="date"]');
    if (input instanceof HTMLInputElement) {
      input.focus();
    }
  }, [editing]);

  if (!editable) {
    const label = row.target_date;
    if (!isMrpTransferRequestEditable(row.status)) {
      return (
        <HoverHintContent hoverHint={TARGET_DATE_LOCKED_HOVER}>
          <span className="tabular-nums text-slate-700">{label}</span>
        </HoverHintContent>
      );
    }
    return <span className="tabular-nums text-slate-700">{label}</span>;
  }

  if (!editing) {
    return (
      <ReadOnlyHoverLayer
        active={!hubWritesAllowed}
        hoverHint={hubReadOnlyHoverHint}
        layout="inline"
      >
        <button
          type="button"
          className={cn(
            "tabular-nums text-slate-700 underline-offset-2 hover:text-brand-800 hover:underline",
            pending && "opacity-70",
          )}
          disabled={pending}
          onClick={onStartEdit}
        >
          {row.target_date}
        </button>
      </ReadOnlyHoverLayer>
    );
  }

  return (
    <div
      ref={editorRef}
      onBlur={(event) => {
        if (pending) return;
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          onDismissEdit();
        }
      }}
    >
      <DeferredCommitDateInput
        value={row.target_date}
        disabled={!hubWritesAllowed}
        pending={pending}
        commitError={commitError}
        onCommit={onCommit}
        className="h-7 w-32 text-xs"
      />
    </div>
  );
}
